import * as React from "react";
import {
  clearAuthSessionStorage,
  emitAuthChanged,
  readAuthSession,
  writeAuthSession,
  type AuthSession,
} from "@/app/services/auth-storage";
import {
  ACCESS_TOKEN_REFRESH_LEAD_MS,
  ensureFreshSession,
} from "@/app/services/api";
import type { MarketplaceUser } from "@/app/types/api";

type AuthContextValue = {
  accessToken: string | null;
  refreshToken: string | null;
  user: MarketplaceUser | null;
  isAuthenticated: boolean;
  setSession: (session: AuthSession) => void;
  clearSession: () => void;
};

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSessionState] = React.useState<AuthSession | null>(() =>
    readAuthSession()
  );

  React.useEffect(() => {
    const sync = () => setSessionState(readAuthSession());
    const onStorage = (e: StorageEvent) => {
      if (!e.key || e.key.startsWith("lumina_mkt_")) sync();
    };
    window.addEventListener("lumina:auth-changed", sync);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("lumina:auth-changed", sync);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  React.useEffect(() => {
    if (!session) return;
    const refreshAt = session.accessTokenExpiresAt - ACCESS_TOKEN_REFRESH_LEAD_MS;
    const delay = Math.max(refreshAt - Date.now(), 0);
    const timer = window.setTimeout(() => {
      void ensureFreshSession().catch(() => undefined);
    }, delay);
    return () => window.clearTimeout(timer);
  }, [session]);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      accessToken: session?.accessToken ?? null,
      refreshToken: session?.refreshToken ?? null,
      user: session?.user ?? null,
      isAuthenticated: !!session?.accessToken && !!session?.user,
      setSession: (next: AuthSession) => {
        writeAuthSession(next);
        setSessionState(next);
        emitAuthChanged();
      },
      clearSession: () => {
        clearAuthSessionStorage();
        setSessionState(null);
        emitAuthChanged();
      },
    }),
    [session]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
